/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { AngularEditorModule } from '@kolkov/angular-editor';
import { ColorPickerModule } from 'ngx-color-picker';
import { NgxFileDropModule } from 'ngx-file-drop';
import { QuillModule } from 'ngx-quill';

import { BlurbTextComponent } from './components/blurb-text/blurb-text.component';

import { BlurbNewComponent } from './components/blurb-new/blurb-new.component';
import { IbisEditorComponent } from './components/ibis-editor/ibis-editor.component';
import { FilterPipe } from './pipes/filter.pipe';
import { CensusProvider, IbisProvider } from './providers';
import { BlurbListComponent } from './components/blurb-list/blurb-list.component';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { IbisReportComponent } from './components/ibis-report/ibis-report.component';
import { PncReportListComponent } from './components/pnc-report-list/pnc-report-list.component';
import { PncReportComponent } from './components/pnc-report/pnc-report.component';

import { FranchiseComponent } from './components/franchise/franchise.component';
import { BlurbService } from './services/blurb.service';
import { UploadPncReportComponent } from './modal/upload-pnc-report/upload-pnc-report.component';
import { AddBlockLocationComponent } from './modal/add-block-location/add-block-location.component';
import { CreateIbisReportComponent } from './modal';
import { CoreModule } from 'src/app/components/fields/core.module';
import { BsDatepickerModule, DatepickerModule } from 'ngx-bootstrap/datepicker';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { BsModalRef, ModalModule } from 'ngx-bootstrap/modal';
import { ResearchRoutingModule } from './research-routing.module';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { OneDigitDecimaNumberDirective } from './directive/decimal.directive';
import { LoaderComponent } from './components/loader/loader.component';
import { InputSearchAdvancedComponent } from './components/input-search-advanced/input-search.component';
import { InputSearchComponent } from './components/input-search/input-search.component';
import { DistrictViewComponent } from './components/district-view/district-view.component';
import { LocationMapComponent } from './components/location-map/location-map.component';
import { LocationStreetViewComponent } from './components/location-street-view/location-street-view.component';
import { EditableTextComponent } from './components/editable-text/editable-text.component';
import { ResearchDetailComponent } from './components/research-detail/research-detail.component';
import { AngularCropperjsModule } from 'angular-cropperjs';
import {StaticTextComponent} from "./components/static-text/static-text.component";
import {GoogleSearchComponent} from "./components/google-search/google-search.component";

@NgModule({
    declarations: [
        BlurbTextComponent,
        BlurbListComponent,
        BlurbNewComponent,
        IbisEditorComponent,
        LoaderComponent,
        FilterPipe,
        SafeHtmlPipe,
        IbisReportComponent,
        PncReportListComponent,
        PncReportComponent,
        UploadPncReportComponent,
        OneDigitDecimaNumberDirective,
        AddBlockLocationComponent,
        CreateIbisReportComponent,
        FranchiseComponent,
        InputSearchAdvancedComponent,
        InputSearchComponent,
        DistrictViewComponent,
        LocationMapComponent,
        LocationStreetViewComponent,
        EditableTextComponent,
        ResearchDetailComponent,
        StaticTextComponent,
        GoogleSearchComponent
    ],
    imports: [
        CommonModule,
        FormsModule,
        CoreModule,
        BsDatepickerModule.forRoot(),
        DatepickerModule.forRoot(),
        ReactiveFormsModule,
        ColorPickerModule,
        BsDropdownModule.forRoot(),
        DragDropModule,
        ModalModule.forRoot(),
        AngularEditorModule,
        ResearchRoutingModule,
        PaginationModule.forRoot(),
        NgxFileDropModule,
        PopoverModule.forRoot(),
        QuillModule.forRoot(),
        AngularCropperjsModule,
    ],
    providers: [
        CensusProvider, IbisProvider, BsModalRef, BlurbService
    ],
    exports: [
        BlurbTextComponent, BlurbListComponent, BlurbNewComponent, IbisEditorComponent
    ]
})

export class ResearchModule {}
